import { useState, useEffect } from "react";
import { FaLock, FaLockOpen, FaEdit, FaBan, FaCheckCircle } from "react-icons/fa";
import { Card, Switch, Select, Message, Spin, Popconfirm } from "@/frontend/components/common";
import { bettingPoolService } from "@/frontend/services";
import { useBettingPoolStatusStore } from "@/frontend/stores";
import type { BettingPoolResponseDTO } from "@/shared/dto";

interface PoolSettingsCardProps {
  pool: BettingPoolResponseDTO;
  isAdmin: boolean;
  onUpdate: () => void;
}

export function PoolSettingsCard({ pool, isAdmin, onUpdate }: PoolSettingsCardProps) {
  const [loadingJoinable, setLoadingJoinable] = useState(false);
  const [loadingPredictable, setLoadingPredictable] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  
  const { statuses, isLoading: loadingStatuses, fetchStatuses } = useBettingPoolStatusStore();

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const handleToggleJoinable = async (checked: boolean) => {
    try {
      setLoadingJoinable(true);
      const response = await bettingPoolService.updateIsJoinable(pool.id, {
        isJoinable: checked,
      });

      if (response.success) {
        Message.success({
          content: checked
            ? "El pool ahora acepta nuevos participantes"
            : "El pool ya no acepta nuevos participantes",
        });
        onUpdate();
      } else {
        Message.error({
          content: response.error || "Error al actualizar el estado",
        });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al actualizar",
      });
    } finally {
      setLoadingJoinable(false);
    }
  };

  const handleTogglePredictable = async (checked: boolean) => {
    try {
      setLoadingPredictable(true);
      const response = await bettingPoolService.updateIsPredictable(pool.id, {
        isPredictable: checked,
      });

      if (response.success) {
        Message.success({
          content: checked
            ? "El pool ahora acepta predicciones"
            : "Las predicciones están deshabilitadas",
        });
        onUpdate();
      } else {
        Message.error({
          content: response.error || "Error al actualizar el estado",
        });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al actualizar",
      });
    } finally {
      setLoadingPredictable(false);
    }
  };

  const handleChangeStatus = async (statusId: string) => {
    try {
      setLoadingStatus(true);
      const response = await bettingPoolService.updateStatus(pool.id, {
        statusId,
      });

      if (response.success) {
        const newStatus = statuses.find((s) => s.id === statusId);
        Message.success({
          content: `Estado actualizado a: ${newStatus?.name || statusId}`,
        });
        onUpdate();
      } else {
        Message.error({
          content: response.error || "Error al cambiar el estado",
        });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al cambiar estado",
      });
    } finally {
      setLoadingStatus(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  const currentStatus = statuses.find((s) => s.id === pool.statusId);

  return (
    <Card className="bg-white border-gray-200">
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <FaCheckCircle className="text-blue-600" />
          Configuración del Pool
        </h3>

        {/* Control de Participantes */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          <div className="flex items-center gap-3">
            {pool.isJoinable ? (
              <FaLockOpen className="text-green-600 text-2xl" />
            ) : (
              <FaLock className="text-red-600 text-2xl" />
            )}
            <div>
              <div className="font-semibold text-gray-800">Acepta participantes</div>
              <div className="text-sm text-gray-600">
                {pool.isJoinable
                  ? "Nuevos usuarios pueden unirse al pool"
                  : "El pool está cerrado a nuevos participantes"}
              </div>
            </div>
          </div>
          <Spin spinning={loadingJoinable}>
            <Popconfirm
              title={
                pool.isJoinable
                  ? "¿Cerrar el pool a nuevos participantes?"
                  : "¿Abrir el pool a nuevos participantes?"
              }
              description={
                pool.isJoinable
                  ? "Los nuevos usuarios no podrán unirse"
                  : "Los nuevos usuarios podrán unirse al pool"
              }
              onConfirm={() => handleToggleJoinable(!pool.isJoinable)}
              okText="Confirmar"
              cancelText="Cancelar"
            >
              <Switch
                checked={pool.isJoinable}
                checkedChildren="Abierto"
                unCheckedChildren="Cerrado"
              />
            </Popconfirm>
          </Spin>
        </div>

        {/* Control de Predicciones */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          <div className="flex items-center gap-3">
            {pool.isPredictable ? (
              <FaEdit className="text-blue-600 text-2xl" />
            ) : (
              <FaBan className="text-orange-600 text-2xl" />
            )}
            <div>
              <div className="font-semibold text-gray-800">Acepta predicciones</div>
              <div className="text-sm text-gray-600">
                {pool.isPredictable
                  ? "Los participantes pueden hacer predicciones"
                  : "Las predicciones están deshabilitadas"}
              </div>
            </div>
          </div>
          <Spin spinning={loadingPredictable}>
            <Popconfirm
              title={
                pool.isPredictable
                  ? "¿Deshabilitar predicciones?"
                  : "¿Habilitar predicciones?"
              }
              description={
                pool.isPredictable
                  ? "Los participantes no podrán hacer nuevas predicciones"
                  : "Los participantes podrán hacer predicciones nuevamente"
              }
              onConfirm={() => handleTogglePredictable(!pool.isPredictable)}
              okText="Confirmar"
              cancelText="Cancelar"
            >
              <Switch
                checked={pool.isPredictable}
                checkedChildren="Habilitado"
                unCheckedChildren="Deshabilitado"
              />
            </Popconfirm>
          </Spin>
        </div>

        {/* Control de Estado */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <FaCheckCircle
              className="text-2xl"
              style={{ color: currentStatus?.color || "#888" }}
            />
            <div>
              <div className="font-semibold text-gray-800">Estado del pool</div>
              <div className="text-sm text-gray-600">
                Define la fase actual del pool
              </div>
            </div>
          </div>
          <Spin spinning={loadingStatus || loadingStatuses}>
            <Select
              value={pool.statusId}
              onChange={handleChangeStatus}
              className="w-full"
              size="large"
              disabled={loadingStatuses || statuses.length === 0}
              options={statuses.map((status) => ({
                value: status.id,
                label: (
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: status.color || "#888",
                      }}
                    />
                    {status.name}
                  </span>
                ),
              }))}
            />
          </Spin>
        </div>
      </div>
    </Card>
  );
}
